import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import TestSeriesFilterDropdown from './TestSeriesFilterDropdown';

export const TEST_SERIES_FILTER = {
  SUBSCRIPTION: 'subscription',
  EDUCATOR: 'educator',
  TEST_TYPE: 'test_type',
  STATUS: 'status',
  ENROLLMENT: 'enrollment'
};

const EMPTY_FILTER_CONFIG = [
  {
    id: TEST_SERIES_FILTER.SUBSCRIPTION,
    defaultLabel: 'Subscription type',
    allLabel: 'All subscription types',
    multipleLabel: (count) => `${count} subscription types`,
    options: []
  },
  {
    id: TEST_SERIES_FILTER.EDUCATOR,
    defaultLabel: 'Educator',
    allLabel: 'all educators',
    multipleLabel: (count) => `${count} educators`,
    options: []
  },
  {
    id: TEST_SERIES_FILTER.TEST_TYPE,
    defaultLabel: 'Test type',
    allLabel: 'All test types',
    multipleLabel: (count) => `${count} test types`,
    options: []
  },
  {
    id: TEST_SERIES_FILTER.STATUS,
    defaultLabel: 'Test series',
    allLabel: 'All test series',
    multipleLabel: (count) => `${count} test series`,
    options: []
  },
  {
    id: TEST_SERIES_FILTER.ENROLLMENT,
    defaultLabel: 'Enrollment',
    allLabel: 'All enrollments',
    multipleLabel: (count) => `${count} enrollment types`,
    options: []
  }
];

const getOptionDisplayName = (option, filterId) =>
  filterId === TEST_SERIES_FILTER.EDUCATOR ? option.name : option.label;

export const getFilterPillLabel = (filter, selectedIds = []) => {
  const count = selectedIds.length;
  const total = filter.options.length;

  if (count === 0) {
    return filter.defaultLabel;
  }

  if (count === 1) {
    const selectedOption = filter.options.find(
      (option) => option.id === selectedIds[0]
    );

    return selectedOption
      ? getOptionDisplayName(selectedOption, filter.id)
      : filter.defaultLabel;
  }

  if (total > 0 && count === total) {
    return filter.allLabel;
  }

  return filter.multipleLabel(count);
};

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Spacings.SPACING_3B};

  ${mediaQuery.uptoMobile} {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    margin: 0 -${Spacings.SPACING_4B};
    padding: 0 ${Spacings.SPACING_4B};

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
`;

const OptionRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  padding: ${Spacings.SPACING_2B} 0;
  border: none;
  cursor: pointer;
  background: transparent;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const Checkbox = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 2px solid
    ${({ $checked }) => ($checked ? 'var(--color-accent-blue)' : '#e9eef2')};
  border-radius: 4px;
  background: ${({ $checked }) =>
    $checked ? 'var(--color-accent-blue)' : 'transparent'};

  &::after {
    content: '';
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
    width: 6px;
    height: 10px;
    margin-bottom: 2px;
    border: solid var(--color-i-white);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const EducatorAvatar = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 48px;
  background-color: #e9eef2;
  background-image: ${({ $src }) => ($src ? `url(${$src})` : 'none')};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const OptionText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const OptionTitle = styled.span`
  font-size: 14px;
  line-height: normal;
  font-weight: ${Fonts.SEMIBOLD};
  color: #364963;
`;

const OptionMeta = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-weight: ${Fonts.REGULAR};
  color: #607798;
`;

const createEmptySelections = () => ({
  [TEST_SERIES_FILTER.SUBSCRIPTION]: [],
  [TEST_SERIES_FILTER.EDUCATOR]: [],
  [TEST_SERIES_FILTER.TEST_TYPE]: [],
  [TEST_SERIES_FILTER.STATUS]: [],
  [TEST_SERIES_FILTER.ENROLLMENT]: []
});

const TestSeriesFilters = ({
  filterConfig,
  appliedSelections,
  onAppliedSelectionsChange
}) => {
  const isMobile = useIsMobile();
  const [openFilterId, setOpenFilterId] = useState(null);
  const [draftSelections, setDraftSelections] = useState(createEmptySelections);
  const resolvedFilterConfig = filterConfig?.length
    ? filterConfig
    : EMPTY_FILTER_CONFIG;

  const filterConfigSignature = useMemo(
    () =>
      resolvedFilterConfig
        .map(
          (filter) =>
            `${filter.id}:${filter.options
              .map((option) => option.id)
              .join(',')}`
        )
        .join('|'),
    [resolvedFilterConfig]
  );

  useEffect(() => {
    setOpenFilterId(null);
    setDraftSelections(createEmptySelections());
  }, [filterConfigSignature]);

  const toggleDraftValue = (filterId, optionId) => {
    setDraftSelections((prev) => {
      const current = prev[filterId] || [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];

      return { ...prev, [filterId]: next };
    });
  };

  const openFilter = (filterId) => {
    setDraftSelections((prev) => ({
      ...prev,
      [filterId]: [...(appliedSelections[filterId] || [])]
    }));
    setOpenFilterId(filterId);
  };

  const closeFilter = () => setOpenFilterId(null);

  const handleToggle = (filterId, shouldOpen) => {
    if (shouldOpen) {
      openFilter(filterId);
      return;
    }
    closeFilter();
  };

  const handleApply = (filterId) => {
    onAppliedSelectionsChange({
      ...appliedSelections,
      [filterId]: [...(draftSelections[filterId] || [])]
    });
    closeFilter();
  };

  const handleClear = (filterId) => {
    setDraftSelections((prev) => ({ ...prev, [filterId]: [] }));
  };

  const renderSimpleOption = (filterId, option) => {
    const isChecked = (draftSelections[filterId] || []).includes(option.id);

    return (
      <OptionRow
        key={option.id}
        type="button"
        onClick={() => toggleDraftValue(filterId, option.id)}
      >
        <Checkbox $checked={isChecked} aria-hidden />
        <OptionTitle>{option.label}</OptionTitle>
      </OptionRow>
    );
  };

  const renderEducatorOption = (option) => {
    const isChecked = (
      draftSelections[TEST_SERIES_FILTER.EDUCATOR] || []
    ).includes(option.id);

    return (
      <OptionRow
        key={option.id}
        type="button"
        onClick={() => toggleDraftValue(TEST_SERIES_FILTER.EDUCATOR, option.id)}
      >
        <Checkbox $checked={isChecked} aria-hidden />
        <EducatorAvatar $src={option.avatar} />
        <OptionText>
          <OptionTitle>{option.name}</OptionTitle>
          {option.meta ? <OptionMeta>{option.meta}</OptionMeta> : null}
        </OptionText>
      </OptionRow>
    );
  };

  const renderOptions = (filter) => {
    if (filter.id === TEST_SERIES_FILTER.EDUCATOR) {
      return (
        <OptionsList>
          {filter.options.map((option) => renderEducatorOption(option))}
        </OptionsList>
      );
    }

    return (
      <OptionsList>
        {filter.options.map((option) => renderSimpleOption(filter.id, option))}
      </OptionsList>
    );
  };

  return (
    <FiltersRow>
      {resolvedFilterConfig.map((filter) => {
        const isOpen = openFilterId === filter.id;
        const selectedIds = appliedSelections[filter.id] || [];
        const hasSelection = selectedIds.length > 0;
        const pillLabel = getFilterPillLabel(filter, selectedIds);

        return (
          <TestSeriesFilterDropdown
            key={filter.id}
            label={pillLabel}
            isOpen={isOpen}
            hasSelection={hasSelection}
            isMobile={isMobile}
            onToggle={(shouldOpen) => handleToggle(filter.id, shouldOpen)}
            onClear={() => handleClear(filter.id)}
            onApply={() => handleApply(filter.id)}
          >
            {renderOptions(filter)}
          </TestSeriesFilterDropdown>
        );
      })}
    </FiltersRow>
  );
};

export default TestSeriesFilters;

TestSeriesFilters.propTypes = {
  filterConfig: PropTypes.arrayOf(PropTypes.object),
  appliedSelections: PropTypes.shape({
    [TEST_SERIES_FILTER.SUBSCRIPTION]: PropTypes.arrayOf(PropTypes.string),
    [TEST_SERIES_FILTER.EDUCATOR]: PropTypes.arrayOf(PropTypes.string),
    [TEST_SERIES_FILTER.TEST_TYPE]: PropTypes.arrayOf(PropTypes.string),
    [TEST_SERIES_FILTER.STATUS]: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onAppliedSelectionsChange: PropTypes.func.isRequired
};

TestSeriesFilters.defaultProps = {
  filterConfig: EMPTY_FILTER_CONFIG
};
